/**
 * usePickerBatches Hook
 *
 * Simplified hook for the Picker page to fetch batch list.
 * Wraps the underlying tRPC query with a cleaner interface.
 *
 * CRITICAL: Uses correct database enum values:
 * BatchStatus = 'OPEN' | 'PICKING' | 'PICKED' | 'CATCHING' | 'PACKING' | 'COMPLETED' | 'CANCELLED'
 */

import { trpc } from '@/lib/trpc';
import type { BatchStatus } from '../types';

interface UsePickerBatchesOptions {
  locationId?: string;
  status?: BatchStatus;
  limit?: number;
  offset?: number;
}

/**
 * Hook for fetching batch list in Picker page
 *
 * @param options.locationId - Required location ID to filter batches
 * @param options.status - Optional status filter (OPEN, PICKING, etc.)
 * @param options.limit - Max batches to fetch (default 50)
 * @param options.offset - Pagination offset (default 0)
 */
export function usePickerBatches(options: UsePickerBatchesOptions) {
  return trpc.productionOrders.listBatches.useQuery(
    {
      locationId: options.locationId!,
      status: options.status,
      limit: options.limit ?? 50,
      offset: options.offset ?? 0,
    },
    {
      enabled: !!options.locationId,
      staleTime: 30_000, // 30 seconds - batches change frequently
      refetchOnWindowFocus: true,
    }
  );
}
